import { Component, computed, inject, input } from '@angular/core';
import { AbsenceOptionUnit, type AbsenceOptionUnitType } from '@app/enums';
import { AbsenceTooltipComponent } from '@app/shared/absence/absence-tooltip/absence-tooltip.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipDirective } from '@sb/tooltip';
import {
  formatDate,
  formatDateRange,
  formatDecimalDuration,
  localeCompareStrings,
  parseDate,
  toISODateString,
} from '@shiftbase-com/ts-shared/utilities';
import { endOfMonth, startOfMonth } from 'date-fns';

import { AbsenceRequestDirective } from '../../../../../+modals/employee/absence-request/absence-request.directive';
import { AbsenceStatus, type EnhancedAbsenceModel } from '../../../../../reducers/orm/absence/absence.model';
import { LocaleService } from '../../../../shared/locale/locale.service';

interface AbsenceBlock {
  absence: EnhancedAbsenceModel;
  // The month-clipped bits that can't be read off `absence` directly; everything else the
  // template derives from `absence`.
  dateLabel: string;
  duration: string;
}

interface AbsenceMonth {
  index: number;
  name: string;
  blocks: AbsenceBlock[];
}

// A full-year overview: every month is a box. Each absence renders as a single block showing its
// date range and duration — a multi-day range is one block, not one row per day.
@Component({
  selector: 'absence-year',
  templateUrl: './absence-year.component.html',
  imports: [TranslatePipe, TooltipDirective, AbsenceTooltipComponent, AbsenceRequestDirective],
})
export class AbsenceYearComponent {
  #locale = inject(LocaleService);

  year = input.required<string>();
  pendingAbsences = input<EnhancedAbsenceModel[]>([]);
  reviewedAbsences = input<EnhancedAbsenceModel[]>([]);

  // Declined absences never appear; the rest are treated uniformly and split by status only for styling.
  #activeAbsences = computed<EnhancedAbsenceModel[]>(() => [
    ...this.reviewedAbsences().filter((absence) => absence.status !== AbsenceStatus.DECLINED),
    ...this.pendingAbsences(),
  ]);

  months = computed<AbsenceMonth[]>(() => {
    const year = Number(this.year());
    const absences = this.#activeAbsences();
    return Array.from({ length: 12 }, (_, monthIndex) => this.#buildMonth(year, monthIndex, absences));
  });

  #buildMonth(year: number, monthIndex: number, absences: EnhancedAbsenceModel[]): AbsenceMonth {
    const locale = this.#locale.getCurrentLocale();
    const monthDate = new Date(year, monthIndex, 1);
    const monthStart = toISODateString(startOfMonth(monthDate));
    const monthEnd = toISODateString(endOfMonth(monthDate));

    // An absence appears in every month its range overlaps (ISO date strings compare chronologically).
    const blocks = absences
      // An open-ended absence (flagged or missing an enddate) has no known end, so it overlaps
      // every month from its start; otherwise it overlaps months up to its enddate.
      .filter(
        (absence) =>
          absence.startdate <= monthEnd && (absence.open_ended || !absence.enddate || absence.enddate >= monthStart),
      )
      .toSorted(
        (a, b) =>
          localeCompareStrings(a.startdate, b.startdate) ||
          Number(a.absenteeOption.weight) - Number(b.absenteeOption.weight),
      )
      .map((absence) => this.#buildBlock(absence, locale, monthStart, monthEnd));

    return { index: monthIndex, name: formatDate(monthDate, locale, 'month'), blocks };
  }

  #buildBlock(absence: EnhancedAbsenceModel, locale: string, monthStart: string, monthEnd: string): AbsenceBlock {
    // Clip the range to this month so a cross-month absence reads accurately in each box it spans.
    const start = absence.startdate > monthStart ? absence.startdate : monthStart;
    const end = absence.enddate < monthEnd ? absence.enddate : monthEnd;
    const openEnded = !!absence.open_ended || !absence.enddate;

    return {
      absence,
      dateLabel: this.#dateLabel(start, end, openEnded, locale),
      duration: this.#duration(absence, monthStart, monthEnd, locale),
    };
  }

  #dateLabel(start: string, end: string, openEnded: boolean, locale: string): string {
    // Open-ended absences have no known end, so show only the (clipped) start — the template
    // appends "unknown". Otherwise show a single day or the clipped range.
    if (openEnded) {
      return formatDate(parseDate(start), locale, 'compact');
    }
    if (start === end) {
      return formatDate(parseDate(start), locale, 'weekdayShort');
    }
    return formatDateRange(parseDate(start), parseDate(end), locale, 'compact');
  }

  #duration(absence: EnhancedAbsenceModel, monthStart: string, monthEnd: string, locale: string): string {
    const unit = absence.absenteeOption.unit ?? AbsenceOptionUnit.HOURS;
    // Whole absence within this month → use its total; otherwise sum only the days that fall here,
    // so a cross-month absence isn't counted in full in every month box it touches.
    const withinMonth = absence.startdate >= monthStart && absence.enddate <= monthEnd;
    const value = withinMonth
      ? Number(unit === AbsenceOptionUnit.DAYS ? absence.day_total : absence.hours)
      : this.#sumAbsenteeDays(absence, monthStart, monthEnd, unit);
    return Number.isNaN(value) || value === 0 ? '' : formatDecimalDuration(value, locale, { preset: 'narrow', unit });
  }

  #sumAbsenteeDays(
    absence: EnhancedAbsenceModel,
    monthStart: string,
    monthEnd: string,
    unit: AbsenceOptionUnitType,
  ): number {
    return Object.entries(absence.AbsenteeDay ?? {})
      .filter(([date, day]) => date >= monthStart && date <= monthEnd && !day.hidden)
      .reduce((sum, [, day]) => {
        const parsed = parseFloat((unit === AbsenceOptionUnit.DAYS ? day.days : day.hours) ?? '');
        return sum + (Number.isNaN(parsed) ? 0 : parsed);
      }, 0);
  }
}
